import { create } from 'zustand'
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  orderBy
} from 'firebase/firestore'
import { db } from '@/config/firebase'
import { derived } from '@/lib/storeHelpers'
import { getPickupCityDisplayName } from '@/utils/cityDisplay'
import {
  LEGACY_SOUTH_SHORE_CITY_ID,
  SOUTH_SHORE_CITY_ID,
  ANNAPOLIS_VALLEY_CITY_ID,
  normalizeCityId,
  normalizeCityRecord
} from '@/utils/cityIds'

// Cache configuration
const CACHE_KEY = 'cities_cache'
const CACHE_TIMESTAMP_KEY = 'cities_cache_timestamp'
const CACHE_DURATION_MS = 24 * 60 * 60 * 1000 // 24 hours
const LAST_PICKUP_CHECKIN_CITY_KEY = 'lastPickupCheckInCityId'

const FIRESTORE_QUERY_TIMEOUT_MS = 12000
const useFirestoreEmulator =
  import.meta.env.PROD !== true &&
  import.meta.env.VITE_USE_FIRESTORE_EMULATOR === 'true'

/** When the Firestore emulator is empty (no `cities` docs), Register / city pick still need options */
const EMULATOR_FALLBACK_CITIES = [
  {
    id: 'halifax',
    name: 'Halifax',
    displayName: 'Halifax Pickup Hockey',
    slug: 'halifax',
    logo: '/logos/halifax.png',
    contactEmail: 'halifaxpickuphockey@gmail.com',
    primaryColor: '#4f001e',
    isActive: true
  },
  {
    id: SOUTH_SHORE_CITY_ID,
    name: 'South Shore',
    displayName: 'South Shore Pickup Hockey',
    slug: SOUTH_SHORE_CITY_ID,
    logo: '/logos/bridgewater.png',
    contactEmail: 'southshorepickuphockey@gmail.com',
    primaryColor: '#1a4d1a',
    regionLabel: 'Bridgewater & Liverpool, NS',
    isActive: true
  },
  {
    id: ANNAPOLIS_VALLEY_CITY_ID,
    name: 'Annapolis Valley',
    displayName: 'Annapolis Valley Pickup Hockey',
    slug: ANNAPOLIS_VALLEY_CITY_ID,
    logo: '/logos/avph.png',
    contactEmail: 'halifaxpickuphockey@gmail.com',
    primaryColor: '#0B2C5E',
    regionLabel: 'Wolfville, NS',
    shortName: 'AVPH',
    isActive: true
  }
]

function withTimeout<T> (promise: Promise<T>, ms: number, label: string): Promise<T> {
  return Promise.race([
    promise,
    new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms)
    })
  ])
}

export type CityResult = { success: boolean, error?: string, [key: string]: any }

export interface CityState {
  // State
  cities: any[]
  currentCity: any
  loading: boolean
  citiesLoaded: boolean

  // Computed
  activeCities: any[]
  currentCityId: string | null
  currentCityDisplayName: string
  currentCityLogo: string
  currentCityEmail: string
  currentCityColor: string

  // Actions
  loadCities: (forceRefresh?: boolean) => Promise<CityResult>
  refreshCities: () => Promise<CityResult>
  clearCache: () => void
  setCurrentCity: (cityId?: string | null) => Promise<CityResult>
  getCityById: (cityId?: string | null) => any
  getLastCityId: () => string | null
  rememberPickupCheckInCity: (cityId?: string | null) => void
  getLastPickupCheckInCityId: () => string | null
  clearCurrentCity: () => void
  createCity: (cityData: any) => Promise<CityResult>
  updateCity: (cityId: string, cityData: any) => Promise<CityResult>
  initializeDefaultCities: () => Promise<CityResult>
}

export const useCityStore = create<CityState>()(
  derived<CityState>((s) => ({
    activeCities: s.cities.filter(city => city.isActive),
    currentCityId: s.currentCity?.id || null,
    currentCityDisplayName: getPickupCityDisplayName(s.currentCity),
    currentCityLogo: s.currentCity?.logo || '/logos/default.png',
    currentCityEmail: s.currentCity?.contactEmail || '',
    currentCityColor: s.currentCity?.primaryColor || '#4f001e'
  }))((set, get) => {
    // Check if cache is valid (exists and not expired)
    const isCacheValid = () => {
      try {
        const timestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY)
        if (!timestamp) return false

        const cacheAge = Date.now() - parseInt(timestamp, 10)
        return cacheAge < CACHE_DURATION_MS
      } catch {
        return false
      }
    }

    // Load cities from localStorage cache
    const loadFromCache = () => {
      try {
        const cached = localStorage.getItem(CACHE_KEY)
        if (cached) {
          return JSON.parse(cached)
        }
      } catch (error) {
        console.warn('Error reading cities cache:', error)
      }
      return null
    }

    // Save cities to localStorage cache
    const saveToCache = (citiesData) => {
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify(citiesData))
        localStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString())
      } catch (error) {
        console.warn('Error saving cities cache:', error)
      }
    }

    // Clear the cities cache
    const clearCache = () => {
      try {
        localStorage.removeItem(CACHE_KEY)
        localStorage.removeItem(CACHE_TIMESTAMP_KEY)
      } catch (error) {
        console.warn('Error clearing cities cache:', error)
      }
    }

    // Load cities - tries cache first, then Firestore
    const loadCities = async (forceRefresh = false): Promise<CityResult> => {
      // If already loaded and not forcing refresh, return cached data
      if (get().citiesLoaded && !forceRefresh) {
        return { success: true, fromCache: true }
      }

      // Try loading from cache first (unless force refresh)
      if (!forceRefresh && isCacheValid()) {
        const cachedCities = loadFromCache()
        if (cachedCities && cachedCities.length > 0) {
          let normalized = cachedCities.map((c) => normalizeCityRecord(c))
          if (normalized.some((c) => c.id === SOUTH_SHORE_CITY_ID)) {
            normalized = normalized.filter((c, idx, arr) => {
              if (c.id !== SOUTH_SHORE_CITY_ID) return true
              return arr.findIndex((x) => x.id === SOUTH_SHORE_CITY_ID) === idx
            })
          }
          set({ cities: normalized, citiesLoaded: true })
          return { success: true, fromCache: true }
        }
      }

      // Load from Firestore
      set({ loading: true })
      try {
        const citiesRef = collection(db, 'cities')
        const q = query(citiesRef, orderBy('name', 'asc'))
        const querySnapshot = await withTimeout(
          getDocs(q),
          FIRESTORE_QUERY_TIMEOUT_MS,
          'cities query'
        )

        let citiesData: any[] = querySnapshot.docs.map(doc =>
          normalizeCityRecord({
            id: doc.id,
            ...doc.data()
          })
        )

        if (citiesData.some((c) => c.id === SOUTH_SHORE_CITY_ID)) {
          citiesData = citiesData.filter((c, idx, arr) => {
            if (c.id !== SOUTH_SHORE_CITY_ID) return true
            return arr.findIndex((x) => x.id === SOUTH_SHORE_CITY_ID) === idx
          })
        }

        let usedEmulatorFallback = false
        if (citiesData.length === 0 && useFirestoreEmulator) {
          console.warn('[city] No cities in Firestore; using emulator fallback (seed `cities` for real data)')
          citiesData = EMULATOR_FALLBACK_CITIES.map((c) => ({ ...c }))
          usedEmulatorFallback = true
        }

        set({ cities: citiesData, citiesLoaded: true })

        if (!usedEmulatorFallback) {
          saveToCache(citiesData)
        }

        return { success: true, fromCache: false, emulatorFallback: usedEmulatorFallback }
      } catch (error) {
        console.error('Error loading cities:', error)

        if (useFirestoreEmulator) {
          console.warn('[city] Cities load failed; using emulator fallback:', error.message)
          set({ cities: EMULATOR_FALLBACK_CITIES.map((c) => ({ ...c })), citiesLoaded: true })
          return { success: true, fromCache: false, emulatorFallback: true }
        }

        // If Firestore fails, try to use stale cache as fallback
        const staleCities = loadFromCache()
        if (staleCities && staleCities.length > 0) {
          set({ cities: staleCities, citiesLoaded: true })
          return { success: true, fromCache: true, stale: true }
        }

        return { success: false, error: error.message }
      } finally {
        set({ loading: false })
      }
    }

    // Refresh cities from Firestore (bypasses cache)
    const refreshCities = async () => {
      return loadCities(true)
    }

    const resolveCityFromCache = (cityId) => {
      const cities = get().cities
      const canonical = normalizeCityId(cityId)
      let city = cities.find((c) => c.id === canonical || c.slug === canonical)
      if (!city && canonical === SOUTH_SHORE_CITY_ID) {
        city = cities.find(
          (c) =>
            c.id === LEGACY_SOUTH_SHORE_CITY_ID ||
            c.slug === LEGACY_SOUTH_SHORE_CITY_ID
        )
      }
      return city ? normalizeCityRecord(city) : null
    }

    // Set the current city by ID
    const setCurrentCity = async (cityId): Promise<CityResult> => {
      if (!cityId) {
        set({ currentCity: null })
        return { success: true }
      }

      const canonical = normalizeCityId(cityId)

      if (!get().citiesLoaded) {
        await loadCities()
      }

      let city = resolveCityFromCache(canonical)

      if (!city) {
        try {
          const idsToTry =
            canonical === SOUTH_SHORE_CITY_ID
              ? [SOUTH_SHORE_CITY_ID, LEGACY_SOUTH_SHORE_CITY_ID]
              : [canonical]
          for (const id of idsToTry) {
            const docSnap = await getDoc(doc(db, 'cities', id))
            if (docSnap.exists()) {
              city = normalizeCityRecord({ id: docSnap.id, ...docSnap.data() })
              set({
                cities: [
                  ...get().cities.filter((c) => c.id !== city.id),
                  city
                ]
              })
              saveToCache(get().cities)
              break
            }
          }
        } catch (error) {
          console.error('Error loading city:', error)
          return { success: false, error: error.message }
        }
      }

      if (city) {
        set({
          currentCity: {
            ...city,
            displayName: getPickupCityDisplayName(city)
          }
        })
        localStorage.setItem('lastCityId', city.id)
        return { success: true, city: get().currentCity }
      }

      return { success: false, error: 'City not found' }
    }

    const getCityById = (cityId) => {
      return resolveCityFromCache(cityId)
    }

    // Get the last used city from localStorage
    const getLastCityId = () => {
      const id = localStorage.getItem('lastCityId')
      return id ? normalizeCityId(id) : null
    }

    const rememberPickupCheckInCity = (cityId) => {
      if (!cityId) return
      localStorage.setItem(LAST_PICKUP_CHECKIN_CITY_KEY, normalizeCityId(cityId))
    }

    const getLastPickupCheckInCityId = () => {
      return localStorage.getItem(LAST_PICKUP_CHECKIN_CITY_KEY)
    }

    // Clear current city
    const clearCurrentCity = () => {
      set({ currentCity: null })
    }

    // Admin: Create a new city
    const createCity = async (cityData): Promise<CityResult> => {
      try {
        const cityId = cityData.slug || cityData.name.toLowerCase().replace(/\s+/g, '-')
        const docRef = doc(db, 'cities', cityId)

        await setDoc(docRef, {
          ...cityData,
          id: cityId,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        })

        // Clear cache and reload
        clearCache()
        await loadCities(true)
        return { success: true, cityId }
      } catch (error) {
        console.error('Error creating city:', error)
        return { success: false, error: error.message }
      }
    }

    // Admin: Update a city
    const updateCity = async (cityId, cityData): Promise<CityResult> => {
      try {
        const docRef = doc(db, 'cities', cityId)
        await updateDoc(docRef, {
          ...cityData,
          updatedAt: new Date().toISOString()
        })

        // Clear cache and reload
        clearCache()
        await loadCities(true)

        // Update current city if it was the one modified
        if (get().currentCity?.id === cityId) {
          set({ currentCity: get().cities.find(c => c.id === cityId) })
        }

        return { success: true }
      } catch (error) {
        console.error('Error updating city:', error)
        return { success: false, error: error.message }
      }
    }

    // Initialize cities for first-time setup (creates Halifax and Bridgewater)
    const initializeDefaultCities = async (): Promise<CityResult> => {
      const defaultCities = [
        {
          id: 'halifax',
          name: 'Halifax',
          displayName: 'Halifax Pickup Hockey',
          slug: 'halifax',
          logo: '/logos/halifax.png',
          contactEmail: 'halifaxpickuphockey@gmail.com',
          primaryColor: '#4f001e',
          isActive: true
        },
        {
          id: SOUTH_SHORE_CITY_ID,
          name: 'South Shore',
          displayName: 'South Shore Pickup Hockey',
          slug: SOUTH_SHORE_CITY_ID,
          logo: '/logos/bridgewater.png',
          contactEmail: 'southshorepickuphockey@gmail.com',
          primaryColor: '#1a4d1a',
          regionLabel: 'Bridgewater & Liverpool, NS',
          isActive: true
        },
        {
          id: ANNAPOLIS_VALLEY_CITY_ID,
          name: 'Annapolis Valley',
          displayName: 'Annapolis Valley Pickup Hockey',
          slug: ANNAPOLIS_VALLEY_CITY_ID,
          logo: '/logos/avph.png',
          contactEmail: 'halifaxpickuphockey@gmail.com',
          primaryColor: '#0B2C5E',
          regionLabel: 'Wolfville, NS',
          shortName: 'AVPH',
          isActive: true
        }
      ]

      for (const city of defaultCities) {
        const docRef = doc(db, 'cities', city.id)
        const docSnap = await getDoc(docRef)

        if (!docSnap.exists()) {
          await setDoc(docRef, {
            ...city,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          })
        }
      }

      // Clear cache and reload
      clearCache()
      await loadCities(true)
      return { success: true }
    }

    return {
      // State
      cities: [],
      currentCity: null,
      loading: false,
      citiesLoaded: false,

      // Computed (recomputed by `derived`)
      activeCities: [],
      currentCityId: null,
      currentCityDisplayName: '',
      currentCityLogo: '/logos/default.png',
      currentCityEmail: '',
      currentCityColor: '#4f001e',

      // Actions
      loadCities,
      refreshCities,
      clearCache,
      setCurrentCity,
      getCityById,
      getLastCityId,
      rememberPickupCheckInCity,
      getLastPickupCheckInCityId,
      clearCurrentCity,
      createCity,
      updateCity,
      initializeDefaultCities
    }
  })
)
